import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { conversationHistoryService } from '../../services/conversationHistoryService';
import { MessageContentRenderer } from '../../components/MessageContent';
import type { AiConversationRecord, AiMessageRecord } from '../../types/aiHistory';
import { formatTime } from '@/utils/timeUtils';
import './style.css'; 

interface AiHistoryPageProps {
  /** 点击"跳转"按钮的回调，参数为会话ID */
  readonly onJumpToChat?: (conversationId: number) => void;
  /** 点击关闭/返回的回调 */
  readonly onClose?: () => void;
}

interface HistoryGroup {
  label: string;
  items: AiConversationRecord[];
}

export const AiHistoryPage: React.FC<AiHistoryPageProps> = ({
  onJumpToChat,
  onClose
}) => {
  const [conversations, setConversations] = useState<readonly AiConversationRecord[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<readonly AiMessageRecord[]>([]);
  const [loadingMsg, setLoadingMsg] = useState(false);

  // 加载列表
  useEffect(() => {
    let active = true;
    conversationHistoryService.listConversations({ limit: -1, businessType: 'default' })
      .then(list => {
        if (active) setConversations(list);
      })
      .catch(err => console.error(err));
    return () => { active = false; };
  }, []);

  // 加载详情
  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    let active = true;
    setLoadingMsg(true);
    conversationHistoryService.getMessages({ conversationId: selectedId })
      .then(msgs => {
        if (active) setMessages(msgs);
      })
      .catch(err => console.error(err))
      .finally(() => {
        if (active) setLoadingMsg(false);
      });
    return () => { active = false; };
  }, [selectedId]);

  // 分组逻辑 + 搜索过滤
  const groups = useMemo<HistoryGroup[]>(() => {
    const today: AiConversationRecord[] = [];
    const yesterday: AiConversationRecord[] = [];
    const earlier: AiConversationRecord[] = [];

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const yesterdayStart = todayStart - 86400000;

    // 先过滤
    const filtered = conversations.filter(c => {
        if (!searchText) return true;
        return (c.title || '').toLowerCase().includes(searchText.toLowerCase());
    });

    filtered.forEach(c => {
      const t = new Date(c.date_created).getTime();
      if (t >= todayStart) today.push(c);
      else if (t >= yesterdayStart) yesterday.push(c);
      else earlier.push(c);
    });

    const res: HistoryGroup[] = [];
    if (today.length) res.push({ label: '今天', items: today });
    if (yesterday.length) res.push({ label: '昨天', items: yesterday });
    if (earlier.length) res.push({ label: '更早', items: earlier });
    return res;
  }, [conversations, searchText]);

  const handleDelete = useCallback(async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm('确定要删除这条会话记录吗？')) return;
    try {
      await conversationHistoryService.deleteConversation(id);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (selectedId === id) setSelectedId(null);
    } catch(e) { /* ignore */ }
  }, [selectedId]);

  const handleJump = useCallback((e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    onJumpToChat?.(id);
  }, [onJumpToChat]);

  const selectedConversation = conversations.find(c => c.id === selectedId);

  return (
    <div className="hp-app-container">
      {/* 左侧列表：透明玻璃质感 sidebar */}
      <div className="hp-sidebar">
        {/* 顶部标题与关闭 */}
        <div className="hp-sidebar-header">
           <div className="hp-sidebar-title">
             <i className="fa-solid fa-clock-rotate-left"></i>
             <span>历史归档</span>
           </div>
           {onClose && (
            <button className="hp-icon-btn" onClick={onClose} title="关闭页面">
                <i className="fa-solid fa-xmark"></i>
            </button>
           )}
        </div>

        {/* 搜索框 */}
        <div className="hp-search-box">
             <i className="fa-solid fa-magnifying-glass"></i>
             <input 
                type="text" 
                placeholder="搜索历史会话..." 
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
             />
        </div>
        
        {/* 列表区域 */}
        <div className="hp-list-scroll">
           {groups.map((g, idx) => (
             <div key={idx} className="hp-group">
               <div className="hp-group-label">{g.label}</div>
               {g.items.map(item => (
                 <div 
                    key={item.id} 
                    className={`hp-item ${selectedId === item.id ? 'active' : ''}`}
                    onClick={() => setSelectedId(item.id)}
                 >
                   <div className="hp-item-left">
                       <i className="fa-regular fa-message hp-item-icon"></i>
                       <div className="hp-text-col">
                            <div className="hp-item-title">{item.title || `对话 #${item.id}`}</div>
                            <div className="hp-subtitle">
                                {formatTime(item.date_created)}
                            </div>
                       </div>
                   </div>
                   
                   <div className="hp-item-actions">
                     <button 
                        className="hp-mini-btn jump" 
                        title="继续聊天"
                        onClick={(e) => handleJump(e, item.id)}
                     >
                       <i className="fa-solid fa-share"></i>
                     </button>
                     <button 
                        className="hp-mini-btn delete" 
                        title="删除"
                        onClick={(e) => handleDelete(e, item.id)}
                     >
                       <i className="fa-solid fa-trash"></i>
                     </button>
                   </div>
                 </div>
               ))}
             </div>
           ))}
           {conversations.length === 0 && !searchText && (
             <div className="hp-empty-list">暂无历史记录</div>
           )}
           {conversations.length > 0 && groups.length === 0 && searchText && (
             <div className="hp-empty-list">未找到相关会话</div>
           )}
        </div>
      </div>

      {/* 右侧主内容区域：白色圆角卡片 */}
      <div className="hp-main-content">
          {selectedId ? (
            <>
              {/* 会话头部 */}
              <div className="hp-chat-header">
                <div>
                    <h3>{selectedConversation?.title || '会话详情'}</h3>
                    <div className="hp-chat-meta">
                        <span className="hp-tag">{messages.length} 条消息</span>
                        <span className="hp-tag-time">{selectedConversation ? formatTime(selectedConversation.date_created) : ''}</span>
                    </div>
                </div>
                <button className="hp-header-action-btn" onClick={() => onJumpToChat?.(selectedId)}>
                  <span>继续聊天</span>
                  <i className="fa-solid fa-arrow-right"></i>
                </button>
              </div>

              {/* 消息列表 - 复刻 ChatPage 结构 */}
              <div className="hp-chat-area">
                {loadingMsg ? (
                  <div className="hp-loading">
                      <i className="fa-solid fa-circle-notch fa-spin"></i>
                      <span>加载记录中...</span>
                  </div>
                ) : (
                  messages.map(msg => {
                    const isUser = msg.role === 'user';
                    return (
                        <div key={msg.id} className={`message ${isUser ? 'user' : 'bot'}`}>
                            {/* 机器人头像 */}
                            {/* {!isUser && (
                                <div className="avatar">
                                    <i className="fas fa-sparkles" />
                                </div>
                            )} */}
                            
                            <div className="message-text-group">
                                <div className="message-content">
                                    <MessageContentRenderer content={msg.content} />
                                </div>
                                <span className="timestamp">
                                    {isUser ? '我' : '助手'} · {new Date(msg.date_created).toLocaleTimeString()}
                                </span>
                            </div>
                        </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="hp-no-selection">
              <div className="hp-placeholder-icon">
                  <i className="fa-solid fa-comments"></i>
              </div>
              <p>请在左侧选择一个会话查看历史记录</p>
            </div>
          )}
      </div>
    </div>
  );
};

export default AiHistoryPage;